import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import ConditionalLink from '@plone/volto/components/manage/ConditionalLink/ConditionalLink';
import { flattenToAppURL, isInternalURL } from '@plone/volto/helpers/Url/Url';
import { useDispatch, useSelector } from 'react-redux';
import { searchContent } from '@plone/volto/actions';

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);  
  const day = date.getDate().toString().padStart(2, '0');
  const monthNames = ['GEN', 'FEB', 'MAR', 'APR', 'MAG', 'GIU', 
                     'LUG', 'AGO', 'SET', 'OTT', 'NOV', 'DIC'];
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();

  if (day == 'NaN') return '';

  return `${day} ${month} ${year}`;
};

function formatDateNumber(dateString) {
    if (!dateString) return 'Non specificato';
    
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    
    return `${day}-${month}-${year}`;
}

// Funzione per determinare lo stato di un bando
const getBandoStatus = (item) => {
  const now = new Date();
  const dataApertura = item.data_apertura ? new Date(item.data_apertura) : null;
  const dataScadenza = item.data_scadenza ? new Date(item.data_scadenza) : null;
  const dataChiusura = item.data_chiusura_procedimento ? new Date(item.data_chiusura_procedimento) : null;

  if (dataChiusura && dataChiusura < now) {
    return 'status-closed';
  }
  
  if (
    (!dataChiusura || dataChiusura >= now) &&
    dataScadenza &&
    dataScadenza < now
  ) {
    return 'status-in-progress';
  } 
  
  if (dataScadenza && dataScadenza >= now) {
    return 'status-active';
  }

  if (dataApertura && dataApertura > now) {
    return 'status-scheduled';
  }

  return 'status-unknown';
};

const BandiCardTemplate = ({  
  linkTitle,
  linkHref,
  isEditMode,
  linkText, 
  linkUrl, 
  alignButtonRight,
  showFourColums = false,
  showPublishDate = false,
  showReadMore = false,
  readMoreText = 'Vedi tutto',
  cardBackground = 'no-background',
  cardIcon,
  querystring = {},
  data = {},
  filterStatus = [] // Nuovo parametro per il filtro stato
}) => {
  const [bandi, setBandi] = useState([]);

  let link = null;
  let href = linkHref?.[0]?.['@id'] || '';

  // CORREZIONE: Applica flattenToAppURL anche per i link interni
  if (isInternalURL(href)) {
    const flattenedHref = flattenToAppURL(href);
    link = (
      <ConditionalLink to={flattenedHref} condition={!isEditMode}>
        {linkTitle || href}
      </ConditionalLink>
    );
  } else if (href) {
    link = <a href={href}>{linkTitle || href}</a>;
  }
  
  const iconName = data?.cardIcon || cardIcon;

  // Calcola la larghezza minima in base al numero di colonne
  const minWidth = showFourColums ? '220px' : '280px';

  const blockId = data?.block || 'bando';
  const dispatch = useDispatch();

  // Stato della ricerca
  const searchResults = useSelector((state) => state.search.subrequests?.[blockId] || {});
  const { items = [], loading, error } = searchResults;

  // Effettua la ricerca
  useEffect(() => {
    if (querystring?.query && Array.isArray(querystring.query)) {
      const searchParams = {
        fullobjects: 1,
        metadata_fields: ['tipologia_bando', 'termine_chiarimenti', 'data_apertura', 'data_chiusura_procedimento', 'data_scadenza'],
      };
  
      querystring.query.forEach((crit) => {
        if (crit.i && crit.v !== undefined) {
          switch (crit.i) {
            case 'sort_on':
            case 'sort_order':
            case 'b_size':
              searchParams[crit.i] = Array.isArray(crit.v) ? crit.v[0] : crit.v;
              break;
            case 'path':
              // ✅ Usa path come stringa, non come oggetto
              searchParams.path = Array.isArray(crit.v) ? crit.v[0] : crit.v;
              break;
            default:
              searchParams[crit.i] = crit.v;
              break;
          }
        }
      });
  
      // ✅ Applica limit se presente
      if (querystring.limit) {
        const limit = parseInt(querystring.limit);
        if (!isNaN(limit)) {
          searchParams.b_size = limit;
        }
      }

      dispatch(
        searchContent(
          '', // lascia vuoto per usare path nel searchParams
          searchParams,
          blockId,
        )
      );
    }
  }, [dispatch, querystring, blockId]);

  // Filtra gli items in base al filterStatus selezionato
  const filteredItems = useMemo(() => {
    if (!filterStatus || filterStatus.length === 0) {
      // Se non c'è filtro, mostra tutti gli items
      return items;
    }

    return items.filter((item) => {
      const itemStatus = getBandoStatus(item);
      return filterStatus.includes(itemStatus);
    });
  }, [items, filterStatus]);

  const effectiveDate = items?.effective;
  const isValidDate = effectiveDate && !isNaN(new Date(effectiveDate).getTime());

  // Formatta la data
  const dataPubblicazione = new Date(effectiveDate);
  const dayDataPubblicazione = dataPubblicazione.getDate().toString().padStart(2, '0');
  const monthDataPubblicazione = dataPubblicazione.toLocaleString('it-IT', { month: 'long' });
  const yearDataPubblicazione = dataPubblicazione.getFullYear();

  const adesso = new Date();
  let statoBando = "";
  let statoBandoClasse = "";
  
  //console.log('Bandi trovati:', searchResults);
  //console.log('Items filtrati:', filteredItems);
  //console.log('Filtro stato applicato:', filterStatus);

  // CORREZIONE: Gestione corretta del linkUrl
  const processedLinkUrl = linkUrl && isInternalURL(linkUrl) 
    ? flattenToAppURL(linkUrl) 
    : linkUrl;

  return (
    <div className={`full-width-background-wrapper ${cardBackground}`}>
      <div className={`simple-card-container`}>
        <div className="simple-card-listing"
          style={{
            gridTemplateColumns: `repeat(auto-fill, minmax(${minWidth}, 1fr))`
          }}
        >
          {filteredItems.map((item) => {
            const processedBando = bandi.find((b) => b.id === item['@id']);

            const now = new Date();

            const dataApertura = item.data_apertura ? new Date(item.data_apertura) : null;
            const dataScadenza = item.data_scadenza ? new Date(item.data_scadenza) : null;
            const dataChiusura = item.data_chiusura_procedimento ? new Date(item.data_chiusura_procedimento) : null;

            let statoBando = 'Non specificato';
            let sfondoStato = '';

            if (dataChiusura && dataChiusura < now) {
              statoBando = 'Chiuso';
              sfondoStato = 'closed';
            } else if (
              (!dataChiusura || dataChiusura >= now) &&
              dataScadenza &&
              dataScadenza < now
            ) {
              statoBando = 'In corso';
              sfondoStato = 'in-progress';
            } else if (dataScadenza && dataScadenza >= now) {
              statoBando = 'Attivo';
              sfondoStato = 'open';
            } else if (dataApertura && dataApertura > now) {
              statoBando = "Programmato";
              sfondoStato = 'scheduled';
            }

            return (
              <div className="simple-card" key={item['@id']}>
                <div className="card-content">
                  {item.EffectiveDate && (
                    <div className="date-with-icon">
                      <svg>...</svg>
                      <span className="publish-date">{formatDateNumber(item.EffectiveDate)}</span>
                    </div>
                  )}

                  <ConditionalLink item={item} condition={!isEditMode}>
                    <h3 className="card-title">
                      {iconName && (
                        <i className={`af-icon fa-solid fa-${iconName}`}></i>
                      )}
                      {item.title || item.id}
                    </h3>
                  </ConditionalLink>

                  {item.description && (
                    <div className="bando-description">{item.description}</div>
                  )}

                  <div className="bando-lower-section">

                    <div className="bando-dati">
                      <span className="d-flex align-items-baseline bando-dati-info">
                        <div className="bando-dati-label me-3">Tipo:</div>
                        <span className="bando-dati-date">
                          {processedBando?.tipoBando || item.tipologia_bando?.title || item.tipologia_bando || 'Non specificato'}
                        </span>
                      </span>
                    </div>

                    {item.effective && (
                      <div className="bando-dati">
                        <span className="d-flex align-items-baseline bando-dati-info">
                          <div className="bando-dati-label me-3">Pubblicato il:</div>
                          <span className="bando-dati-date">
                            {item.effective ? formatDateNumber(item.effective) : 'Non specificato'}
                          </span>
                        </span>
                      </div>
                    )}

                    {item.data_scadenza && (
                      <div className="bando-dati">
                        <span className="d-flex align-items-baseline bando-dati-info">
                          <div className="bando-dati-label me-3">Scadenza partecipazione:</div>
                          <span className="bando-dati-date">
                            {item.data_scadenza ? formatDateNumber(item.data_scadenza) : 'Non specificato'}
                          </span>
                        </span>
                      </div>
                    )}

                    <div className="bando-dati">
                      <span className="d-flex align-items-baseline bando-dati-info">
                        <div className="bando-dati-label me-3">Stato:</div>
                        <span className="bando-dati-date">
                          <div className={`bando-state ${sfondoStato}`}>{statoBando}</div>
                        </span>
                      </span>
                    </div>

                  </div>

                  {showReadMore && (
                    <div className="read-more-link">
                      <ConditionalLink item={item} condition={!isEditMode}>
                        {readMoreText}
                      </ConditionalLink>
                    </div>
                  )}
                </div>
              </div>
            );
          })}

        </div>

        {processedLinkUrl && (
          <div className={`link-button my-4 ${alignButtonRight ? 'align-right' : ''}`}>
            <a
              href={processedLinkUrl}
              className={`btn btn-primary`}
            >
              {linkText || 'Scopri di più'}
            </a>
          </div>
        )}

        {link && <div className="footer">{link}</div>}
      </div>
    </div>
  );
};

BandiCardTemplate.propTypes = {
  items: PropTypes.arrayOf(PropTypes.any).isRequired,
  linkMore: PropTypes.any,
  isEditMode: PropTypes.bool,
  filterStatus: PropTypes.arrayOf(PropTypes.string),
};

export default BandiCardTemplate;